import React, { useRef, useState } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Link} from 'react-router-dom';
import {
  Waves,
  ArrowRight,
  Sparkles,
  Zap,
  Eye,
  TrendingUp,
  CheckCircle,
  Network,
  Activity,
  Wifi,
  Smartphone,
  Thermometer,
  Gauge,
  Fan,
  Satellite,
} from 'lucide-react';

/**
 * Lightweight image component with fallback handling.
 * Kept inline so this file is fully self-contained.
 */
const ImageWithFallback: React.FC<{
  src: string;
  alt: string;
  className?: string;
  fallbackSrc?: string;
}> = ({ src, alt, className, fallbackSrc }) => {
  const [imgSrc, setImgSrc] = useState(src);
  return (
    <img
      loading="lazy"
      src={imgSrc}
      alt={alt}
      className={className}
      onError={() => {
        if (fallbackSrc && imgSrc !== fallbackSrc) setImgSrc(fallbackSrc);
      }}
    />
  );
};

interface IoTProduct {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  icon: React.ComponentType<any>;
  logo?: string;
  features: string[];
  benefits: string[];
  path: string;
  gradient: string;
  imageUrl: string;
  imageAlt?: string;
}

// ✅ Separate image links for each product (Unsplash CDN). Replace with your own URLs anytime.
const iotProducts: IoTProduct[] = [
  {
    id: 'aiCooler',
    title: 'AI Cooler Smart AC',
    subtitle: 'AI Enhanced Split AC and HVAC Control',
    description:
      'AI Cooler is an AI enhanced split AC platform for OEMs, property portfolios, and data centers. It delivers demand response readiness, supports utility energy rebates, and provides predictive maintenance through cloud analytics. The system optimizes temperature, humidity, and airflow based on real time data and occupancy to reduce energy cost and downtime.',
    icon: Fan,
    logo: '',
    features: [
      'Real time monitoring of temperature, humidity, and occupancy',
      'AI control for adaptive setpoints and airflow optimization',
      'Demand response readiness with utility program integration',
      'Predictive maintenance with cloud analytics and health scores',
      'Fault detection and diagnostic alerts',
      'Web and mobile dashboards for control and insights',
      'Smart scheduling and policy based energy modes'
    ],
    benefits: [
      'Reduces power consumption through intelligent control',
      'Qualifies for utility rebates with demand response capability',
      'Decreases unplanned downtime with predictive maintenance',
      'Improves comfort and asset life across sites',
      'Lowers carbon footprint with data driven optimization'
    ],
    path: '/products/ai/aicooler',
    gradient: 'from-blue-500 to-cyan-600',
    imageUrl: '/assets/Products/iot-Products/AIcooler.png',
    imageAlt: 'AI powered smart AC dashboard showing sensors, control, and energy analytics'
  },
  {
    id: 'AssetManagement',
    title: 'Asset Management',
    subtitle: 'Satellite Linked Tracking and Predictive Analytics',
    description:
      'An IoT asset platform for provisioning, monitoring, and protecting distributed assets. It manages device onboarding, sensor telemetry, edge logic, satellite or cloud relay, policy execution, alerts with field dispatch, and lifecycle analytics. Tiered SaaS plans and data APIs monetize insights and support continuous improvement.',
    icon: Satellite,
    logo: '',
    features: [
      'Device provisioning and secure onboarding',
      'Sensor telemetry ingestion with edge logic',
      'Satellite and cloud relay for remote assets',
      'Policy engine for rules, thresholds, and actions',
      'Real time alerts and automated field dispatch',
      'Lifecycle analytics for health, usage, and costs',
      'Predictive analytics to prevent downtime',
      'Satellite linked tracking for batteries, telecom cabinets, oil and gas kits, and public assets',
      'Tiered SaaS and data APIs for insights monetization'
    ],
    benefits: [
      'Reduce theft and displacement with continuous tracking',
      'Lower downtime through predictive maintenance',
      'Improve visibility across distributed fleets and sites',
      'Accelerate response with automated alerts and dispatch',
      'Gain actionable insights with lifecycle and usage analytics',
      'Scale reliably with satellite connectivity and cloud services'
    ],
    path: '/products/iot/assetManagement',
    gradient: 'from-sky-500 to-indigo-600',
    imageUrl: '/assets/Products/iot-Products/assetManagement.png',
    imageAlt: 'IoT asset management dashboard showing satellite linked tracking, alerts, and analytics'
  },
  {
    id: 'water-level-iot',
    title: 'Water Level IoT',
    subtitle: 'Precise Connected Water Monitoring',
    description:
      'A precise, connected water monitoring solution for residential complexes, factories, farms, and municipalities. High-accuracy sensors track real-time levels with alerts, historical analysis, and optional auto-pump control.',
    icon: Waves,
    logo: '',
    features: [
      'High-accuracy real-time level sensors',
      'Multi-channel alerts (dashboard, mobile, calls)',
      'Historical data analysis and trends',
      'Leak detection and optimization tools',
      'Optional automated pump control',
    ],
    benefits: [
      'Prevent water shortages and overflows',
      'Optimize water usage efficiently',
      'Detect leaks before major damage',
      'Enable unmanned water operations',
    ],
    path: '/products/iot/water-level-iot',
    gradient: 'from-blue-500 to-cyan-600',
    imageUrl:
      '/assets/Products/iot-Products/Water-Level-IoT.png',
    imageAlt: 'Smart water monitoring sensors with dashboard',
  }



];

const FloatingIcon: React.FC<{ icon: React.ComponentType<any>; delay: number }> = ({ icon: Icon, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: [0, 1, 0.7, 1], y: [20, 0, -10, 0], rotate: [0, 5, -5, 0] }}
    transition={{ duration: 4, delay, repeat: Infinity, repeatType: 'reverse' }}
    className="absolute"
  >
    <Icon className="h-5 w-5 text-primary/40 md:h-6 md:w-6" />
  </motion.div>
);

const IoTParticleField: React.FC = () => {
  const icons = [Wifi, Smartphone, Thermometer, Network, Gauge, Activity];
  const positions = [
    { top: '10%', left: '10%' },
    { top: '20%', right: '15%' },
    { top: '40%', left: '5%' },
    { top: '60%', right: '10%' },
    { top: '80%', left: '20%' },
    { top: '15%', left: '50%' },
    { top: '35%', right: '30%' },
    { top: '70%', left: '60%' },
    { top: '25%', right: '50%' },
    { top: '85%', right: '40%' },
    { top: '45%', left: '80%' },
    { top: '65%', left: '30%' },
    { top: '30%', right: '20%' },
    { top: '75%', right: '60%' },
    { top: '55%', left: '40%' },
  ];

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {Array.from({ length: 15 }).map((_, i) => {
        const Icon = icons[i % icons.length];
        const position = positions[i];
        return (
          <div key={i} className="absolute" style={position}>
            <FloatingIcon icon={Icon} delay={i * 0.3} />
          </div>
        );
      })}
    </div>
  );
};

const ProductCard: React.FC<{ product: IoTProduct; index: number }> = ({ product, index }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const Icon = product.icon;

  return (
    <section id={product.id} ref={ref} className="relative scroll-mt-24 py-16 md:py-15 lg:py-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 80 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 80 }}
          transition={{ duration: 0.7, delay: index * 0.05 }}
          className={`grid items-center gap-10 md:gap-14 lg:gap-16 lg:grid-cols-2 ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}
        >
          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: index % 2 === 0 ? -80 : 80 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: index % 2 === 0 ? -80 : 80 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className={`${index % 2 === 1 ? 'lg:col-start-2' : ''}`}
          >
            {/* Header */}
            <div className="mb-6 flex items-center gap-4 sm:mb-8">
              <motion.div
                whileHover={{ rotate: product.logo ? 0 : 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
                className={`neon-glow rounded-xl p-3 sm:p-4 ${product.logo ? 'bg-white/10' : `bg-gradient-to-br ${product.gradient}`}`}
                aria-hidden
              >
                {product.logo ? (
                  <img
                    src={product.logo}
                    alt={`${product.title} logo`}
                    className="h-10 w-10 sm:h-12 sm:w-12 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        parent.className = `neon-glow rounded-xl p-3 sm:p-4 bg-gradient-to-br ${product.gradient}`;
                      }
                    }}
                  />
                ) : (
                  <Icon className="h-6 w-6 text-white sm:h-7 sm:w-7" />
                )}
              </motion.div>
              <div>
                <motion.h2
                  initial={{ opacity: 0, y: 16 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
                  transition={{ duration: 0.5, delay: 0.25 }}
                  className="mb-1 text-2xl font-bold text-white sm:text-3xl lg:text-4xl"
                >
                  {product.title}
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className={`text-md font-semibold text-transparent bg-clip-text bg-gradient-to-r ${product.gradient}`}
                >
                  {product.subtitle}
                </motion.p>
              </div>
            </div>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              className="mb-8 max-w-prose text-base leading-relaxed text-muted-foreground sm:text-lg text-justify"
            >
              {product.description}
            </motion.p>

            {/* Features & Benefits */}
            <div className="mb-8 grid gap-6 md:grid-cols-2 md:gap-8">
              {/* Features */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="glass rounded-xl p-5 sm:p-6"
              >
                <div className="mb-3 flex items-center gap-2 sm:mb-4">
                  <Zap className="h-5 w-5 text-primary" />
                  <h4 className="font-semibold text-white">Key Features</h4>
                </div>
                <ul className="space-y-2">
                  {product.features.map((feature, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ opacity: 0, x: -16 }}
                      animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -16 }}
                      transition={{ duration: 0.35, delay: 0.45 + idx * 0.08 }}
                      className="flex items-start gap-2 text-sm text-muted-foreground"
                    >
                      <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
                      <span>{feature}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>

              {/* Benefits */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.45 }}
                className="glass rounded-xl p-5 sm:p-6"
              >
                <div className="mb-3 flex items-center gap-2 sm:mb-4">
                  <TrendingUp className="h-5 w-5 text-secondary" />
                  <h4 className="font-semibold text-white">Benefits</h4>
                </div>
                <ul className="space-y-2">
                  {product.benefits.map((benefit, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ opacity: 0, x: -16 }}
                      animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -16 }}
                      transition={{ duration: 0.35, delay: 0.5 + idx * 0.08 }}
                      className="flex items-start gap-2 text-sm text-muted-foreground"
                    >
                      <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
                      <span>{benefit}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.55 }}
            >
              <Link
                to={product.path}
                className="group inline-flex items-center gap-3 rounded-xl bg-gradient-to-r from-primary to-secondary px-6 py-3 font-semibold text-white shadow transition-all duration-300 hover:shadow-2xl hover:shadow-primary/25 sm:px-8 sm:py-4"
              >
                Learn More
                <motion.span whileHover={{ x: 4 }} transition={{ duration: 0.2 }}>
                  <ArrowRight className="h-5 w-5" />
                </motion.span>
              </Link>
            </motion.div>
          </motion.div>

          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: index % 2 === 0 ? 80 : -80, rotateY: index % 2 === 0 ? -10 : 10 }}
            animate={isInView ? { opacity: 1, x: 0, rotateY: 0 } : { opacity: 0, x: index % 2 === 0 ? 80 : -80, rotateY: index % 2 === 0 ? -10 : 10 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className={`relative perspective-1000 ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}
          >
            <div className="relative group">
              {/* Glow */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                className={`absolute -inset-6 rounded-full blur-3xl opacity-20 bg-gradient-to-r ${product.gradient}`}
                aria-hidden
              />

              {/* Main Image */}
              <motion.div
                whileHover={{ scale: 1.03, rotateY: 4, rotateX: 2 }}
                transition={{ duration: 0.35 }}
                className="relative z-10 overflow-hidden rounded-2xl border border-white/5 bg-white/5 shadow-xl backdrop-blur-sm"
              >
                <div className="aspect-square w-full">
                  <ImageWithFallback
                    src={product.imageUrl}
                    fallbackSrc="https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=1600&auto=format&fit=crop"
                    alt={product.imageAlt || product.title}
                    className="h-full w-full object-cover"
                  />
                </div>

                {/* Overlay with IoT Elements */}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent">
                  <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6">
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
                      transition={{ duration: 0.45, delay: 0.6 }}
                      className="flex items-center gap-3 text-white"
                    >
                      <div className="rounded-lg bg-primary/20 p-2 backdrop-blur-sm">
                        <Wifi className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">IoT-Connected</p>
                        <p className="text-xs text-white/80 sm:text-sm">Smart Connectivity</p>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>

              {/* Floating Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }}
                transition={{ duration: 0.5, delay: 0.9 }}
                className="absolute -top-4 -right-4 z-10 rounded-xl p-2 glass"
              >
                <div className="flex items-center gap-2 text-primary">
                  <Activity className="h-4 w-4 animate-pulse" />
                  <span className="font-mono text-xs">CONNECTED</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export const IoTProductsPage: React.FC = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '60%']);

  // Note: Scroll-to-section behavior is now handled globally by ScrollToTop component in App.tsx

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div style={{ y: backgroundY }} className="fixed inset-0 opacity-30">
        <IoTParticleField />
      </motion.div>

      {/* Hero Section */}
      <section className="relative flex min-h-[70vh] items-center justify-center sm:min-h-[80vh] lg:min-h-screen">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} className="relative">
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mb-4 text-4xl font-bold leading-tight sm:text-5xl lg:text-7xl"
            >
              <span className="bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
                IoT PRODUCTS
              </span>
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mb-6 flex items-center justify-center gap-3 sm:mb-8 sm:gap-4"
            >
              <div className="h-px w-16 bg-gradient-to-r from-transparent via-primary to-transparent sm:w-24" />
              <Wifi className="h-6 w-6 animate-pulse-glow text-primary sm:h-8 sm:w-8" />
              <div className="h-px w-16 bg-gradient-to-r from-transparent via-primary to-transparent sm:w-24" />
            </motion.div>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              className="mx-auto mb-4 max-w-2xl text-base text-muted-foreground text-center sm:text-lg lg:text-2xl"
            >
              Smart Connectivity for Smarter Decisions
            </motion.p>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55 }}
              className="mx-auto mb-10 max-w-3xl text-sm text-muted-foreground text-center sm:text-base lg:text-lg"
            >
              amasQIS.ai's IoT products bridge physical infrastructure and digital intelligence—capturing real-time data, analyzing it instantly, and triggering automated actions for safety, efficiency, and sustainability.
            </motion.p>

            {/* Quick Navigation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mb-14 flex flex-wrap justify-center gap-3 sm:gap-4"
            >
              {iotProducts.map((product, index) => {
                const Icon = product.icon;
                return (
                  <motion.a
                    key={product.id}
                    href={`#${product.id}`}
                    whileHover={{ scale: 1.04, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.85 + index * 0.06 }}
                    className="glass group rounded-xl px-3 py-2 text-xs hover:glass-strong sm:px-4 sm:py-3 sm:text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-primary transition-colors group-hover:text-accent" />
                      <span className="font-medium">{product.title}</span>
                    </div>
                  </motion.a>
                );
              })}
            </motion.div>

            {/* Scroll Indicator */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1 }} className="flex flex-col items-center gap-2">
              <p className="text-xs text-muted-foreground sm:text-sm">Explore Our IoT Solutions</p>
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="flex h-10 w-6 justify-center rounded-full border-2 border-primary/50"
                aria-hidden
              >
                <motion.div
                  animate={{ y: [0, 12, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="mt-2 h-3 w-1 rounded-full bg-primary"
                />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Product Sections */}
      {iotProducts.map((product, index) => (
        <ProductCard key={product.id} product={product} index={index} />
      ))}

      {/* Bottom CTA */}
      <section className="relative pb-16 pt-6 sm:pb-20 sm:pt-10 lg:pb-24 lg:pt-12">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
            <h2 className="mb-3 text-2xl font-bold sm:mb-4 sm:text-3xl lg:text-4xl">Ready to Connect Your Infrastructure?</h2>
            <p className="mx-auto mb-6 max-w-2xl text-sm text-muted-foreground sm:mb-8 sm:text-base">
              Experience the power of smart connectivity and real-time intelligence.
            </p>
            <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-primary to-secondary px-6 py-3 font-semibold text-white shadow transition-all duration-300 hover:shadow-2xl hover:shadow-primary/25 sm:px-8 sm:py-4"
              >
                Get Started Today
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                className="glass inline-flex items-center justify-center gap-3 rounded-xl px-6 py-3 font-semibold text-white hover:glass-strong sm:px-8 sm:py-4"
              >
                Schedule Demo
                <Eye className="h-5 w-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default IoTProductsPage;